import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import { NewDropdown } from '@/components/Forms/Dropdown';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { InvoicesMemberResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  countries,
  debtorBanks,
  paymentTypes,
  recipientTypes,
} from '@/utils/data/payments';
import { handleFormErrors } from '@/utils/helpers';
import { hexToRgba } from '@/utils/theme';

import InvoicesFreelancerBlock from './InvoicesFreelancerBlock';
import InvoicesSwiftCodesField from './InvoicesSwiftCodesField';

export type FormDataT = {
  create_freelancer: boolean;
  name?: string;
  address: string;
  city: string;
  post_code: string;
  country: string;
  creditor_id: string;
  activity_code?: string;
  vat_number?: string;
  representative?: string;
  company_address?: string;
  account_type?: 'freelancer' | 'other';
  paypal_email?: string;
  payoneer_email?: string;
  banks: {
    iban: string;
    swift_code: string;
    recipient_type: string;
    debtor_bank: string;
    bank: string;
    bank_country: string;
    payment_type: string;
    bank_address: string;
    note: string;
    true_receiver?: string;
    id?: number;
  }[];
};

const InnerFormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
`;

export const RemoveButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    color: ${theme.colors.error};
    background: ${theme.colors.errorLight};
  `}
  grid-column: span 2;
  text-align: center;
  border-radius: 4px;
  border: solid 1px transparent;
  padding: 0 11px;
  height: 27px;
  margin-top: 6px;
  align-items: center;
  font-size: 11px;
  line-height: 15px;
  transition: border-color ease-out 0.2s;
  &:hover {
    border: solid 1px;
  }
`;

const AddButtonHolder = styled.div`
  button {
    width: 100%;
  }
`;

const BanksBlockHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const BankBreak = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.lightGray};
  height: 1px;
  margin-right: 14px;
  margin-left: 18px;
`;

const BankNumber = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
  padding: 0 5px;
  min-width: 50px;
`;

export const BankBox = styled.div`
  grid-column: span 2;
  background: ${({ theme }) => hexToRgba(theme.colors.grayscaleLight, 0.5)};
  padding: 24px 32px;
  margin: 8px -32px 0;
`;

interface Props {
  member?: InvoicesMemberResource;
  onClose: () => void;
  memberId?: number;
}

const InvoicesMemberModal: FC<Props> = ({ onClose, member, memberId }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const { isFetching, data: invoiceMember } = useQuery<InvoicesMemberResource>(
    ['invoices_members', { id: memberId }],
    () =>
      api.get<never, InvoicesMemberResource>(
        `/v1/payments/members/${memberId}`,
      ),
    { enabled: !!memberId },
  );

  const generateDefaultValues = (member?: InvoicesMemberResource) => {
    return {
      create_freelancer: member?.freelancer_id ? true : false,
      name: member?.name ?? '',
      address: member?.address ?? '',
      post_code: member?.post_code ?? '',
      country: member?.country ?? '',
      creditor_id: member?.creditor_id ?? '',
      activity_code: member?.activity_code ?? '',
      vat_number: member?.vat_number ?? '',
      representative: member?.representative ?? '',
      company_address: member?.company_address ?? '',
      city: member?.city ?? '',
      paypal_email: member?.paypal_email ?? '',
      payoneer_email: member?.payoneer_email ?? '',
      banks: member?.banks.length
        ? member?.banks.map((bank) => {
            return {
              iban: bank.iban,
              swift_code: bank.swift_code,
              recipient_type: bank.recipient_type,
              debtor_bank: bank.debtor_bank,
              bank: bank.bank,
              bank_country: bank.bank_country,
              payment_type: bank.payment_type,
              bank_address: bank.bank_address,
              note: bank.note ?? '',
              true_receiver: bank.true_receiver ?? '',
              id: bank.id,
            };
          })
        : [
            {
              iban: '',
              swift_code: '',
              recipient_type: '',
              debtor_bank: '',
              bank: '',
              bank_country: '',
              payment_type: '',
              bank_address: '',
              note: '',
              true_receiver: '',
            },
          ],
    };
  };

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: generateDefaultValues(member),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'banks',
  });

  useEffect(() => {
    if (!!invoiceMember) {
      reset(generateDefaultValues(invoiceMember));
    }
  }, [invoiceMember, reset]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      // A receiver can hold bank details and both wallet emails at once, so
      // everything on the form is saved as typed. Which of them an invoice
      // actually uses is picked per invoice, not stored on the member.
      const payload = data;
      try {
        if (member || invoiceMember) {
          await api.put<typeof payload, InvoicesMemberResource>(
            `/v1/payments/members/${member?.id || invoiceMember?.id}`,
            payload,
          );
        } else {
          await api.post<typeof payload, InvoicesMemberResource>(
            '/v1/payments/members',
            payload,
          );
        }

        queryClient.invalidateQueries(['invoices_list'], {
          exact: false,
        });

        queryClient.refetchQueries(['invoices_members'], {
          exact: true,
        });

        alerts.success(
          `${
            member || invoiceMember
              ? 'Receiver has been succesfully updated'
              : 'New Receiver has been succesfully added'
          }`,
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel
        heading={!!member || !!memberId ? 'Edit member' : 'Add receiver'}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            {!!isFetching ? (
              <ContentSpinner />
            ) : (
              <FormLayout>
                <Larger>
                  <InvoicesFreelancerBlock
                    disabled={!!member}
                    control={control}
                  />
                </Larger>
                <Larger>
                  <Label htmlFor="reveicerName">Receiver name</Label>
                  <Input
                    id="reveicerName"
                    {...register('name')}
                    placeholder="Add name"
                    $hasError={!!errors.name}
                  />
                  {!!errors.name && (
                    <ValidationMessage>{errors.name.message}</ValidationMessage>
                  )}
                </Larger>
                <FormField<FormDataT>
                  name="paypal_email"
                  control={control}
                  label="Paypal email"
                  placeholder="Add email"
                />
                <FormField<FormDataT>
                  name="payoneer_email"
                  control={control}
                  label="Payoneer email"
                  placeholder="Add email"
                />
                <Larger>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    {...register('address')}
                    id="address"
                    placeholder="Add address"
                    $hasError={!!errors.address}
                  />
                  {!!errors.address && (
                    <ValidationMessage>
                      {errors.address.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="city">City</Label>
                  <Input
                    {...register('city')}
                    id="city"
                    placeholder="Add city"
                    $hasError={!!errors.city}
                  />
                  {!!errors.city && (
                    <ValidationMessage>{errors.city.message}</ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    {...register('post_code')}
                    id="postcode"
                    placeholder="Add Postcode"
                    $hasError={!!errors.post_code}
                  />
                  {!!errors.post_code && (
                    <ValidationMessage>
                      {errors.post_code.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="country">Country</Label>
                  <NewDropdown
                    id="country"
                    name="country"
                    options={countries}
                    control={control}
                  />
                  {!!errors.country && (
                    <ValidationMessage>
                      {errors.country.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="creditorId">Creditor Id</Label>
                  <Input
                    {...register('creditor_id')}
                    id="creditorId"
                    placeholder="Add ID"
                    $hasError={!!errors.creditor_id}
                  />
                  {!!errors.creditor_id && (
                    <ValidationMessage>
                      {errors.creditor_id.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="activityCode">Activity Code</Label>
                  <Input
                    {...register('activity_code')}
                    id="activityCode"
                    placeholder="Add activity code"
                    $hasError={!!errors.activity_code}
                  />
                  {!!errors.activity_code && (
                    <ValidationMessage>
                      {errors.activity_code.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="vatNumber">VAT Number</Label>
                  <Input
                    {...register('vat_number')}
                    id="vatNumber"
                    placeholder="Add VAT number"
                    $hasError={!!errors.vat_number}
                  />
                  {!!errors.vat_number && (
                    <ValidationMessage>
                      {errors.vat_number.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="representative">Representative</Label>
                  <Input
                    {...register('representative')}
                    id="representative"
                    placeholder="Add representative"
                    $hasError={!!errors.representative}
                  />
                  {!!errors.representative && (
                    <ValidationMessage>
                      {errors.representative.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="companyAddress">Company Address</Label>
                  <Input
                    {...register('company_address')}
                    id="companyAddress"
                    placeholder="Add company address"
                    $hasError={!!errors.company_address}
                  />
                  {!!errors.company_address && (
                    <ValidationMessage>
                      {errors.company_address.message}
                    </ValidationMessage>
                  )}
                </Larger>
                {fields.map((_, index) => (
                  <BankBox key={index}>
                    <BanksBlockHeading>
                      <Text $type="caption">Bank details</Text>
                      <BankBreak />
                      <BankNumber>
                        <Text $type="smallBold" $color="white">
                          Nr. {index + 1}
                        </Text>
                      </BankNumber>
                    </BanksBlockHeading>
                    <InnerFormLayout>
                      <FormField<FormDataT>
                        name={`banks.${index}.true_receiver`}
                        control={control}
                        label="True Receiver"
                        placeholder="Add true receiver"
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.debtor_bank`}
                        control={control}
                        type="select"
                        label="Debtor bank"
                        options={debtorBanks}
                      />
                      <InvoicesSwiftCodesField
                        control={control}
                        name={`banks.${index}.swift_code`}
                        bankFieldName={`banks.${index}.bank`}
                        ibanFieldName={`banks.${index}.iban`}
                        countryFieldName={`banks.${index}.bank_country`}
                        paymentTypeFieldName={`banks.${index}.payment_type`}
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.recipient_type`}
                        control={control}
                        type="select"
                        label="Recipient type"
                        options={recipientTypes}
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.payment_type`}
                        control={control}
                        type="select"
                        label="Payment type"
                        options={paymentTypes}
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.bank_country`}
                        control={control}
                        type="select"
                        label="Bank country"
                        options={countries}
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.bank_address`}
                        control={control}
                        label="Bank address"
                        placeholder="Add address"
                      />
                      <FormField<FormDataT>
                        name={`banks.${index}.note`}
                        control={control}
                        label="Note"
                        type="textarea"
                        placeholder="Add note"
                      />
                      {index > 0 && (
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove section
                        </RemoveButton>
                      )}
                    </InnerFormLayout>
                  </BankBox>
                ))}
                <Larger>
                  <AddButtonHolder>
                    <Button
                      type="button"
                      $type="colorized"
                      onClick={() =>
                        append({
                          iban: '',
                          swift_code: '',
                          recipient_type: '',
                          debtor_bank: '',
                          bank: '',
                          bank_country: '',
                          payment_type: '',
                          bank_address: '',
                          note: '',
                          true_receiver: '',
                        })
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 8H14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add other bank
                    </Button>
                  </AddButtonHolder>
                </Larger>
              </FormLayout>
            )}
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {!!member ? 'Update' : 'Save'}
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default InvoicesMemberModal;
